import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, CircleDot } from "lucide-react";
import { useApp } from "../context/AppContext";
import { gamesApi } from "../lib/api";
import { inr } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { WinReveal } from "../components/WinReveal";
import { playCoinSplash, unlockAudio } from "../lib/rouletteSound";

const ROWS = 8;
const MULT = [11, 2.4, 1.1, 0.4, 0.2, 0.4, 1.1, 2.4, 11]; // matches backend
// International-standard Plinko colouring: hot red/orange on the high edges,
// cooling to green/blue for the low-payout centre.
const bucketStyle = (m) => {
  if (m >= 10) return { background: "linear-gradient(180deg,#f43f5e,#b91c1c)", color: "#fff", glow: "244,63,94" };
  if (m >= 2)  return { background: "linear-gradient(180deg,#fb923c,#c2410c)", color: "#fff", glow: "251,146,60" };
  if (m >= 1)  return { background: "linear-gradient(180deg,#fbbf24,#b45309)", color: "#1a1205", glow: "251,191,36" };
  return { background: "linear-gradient(180deg,#22d3ee,#0e7490)", color: "#04222b", glow: "34,211,238" };
};

export default function Plinko() {
  const { isAuthed, balance, refresh } = useApp();
  const { toast } = useToast();
  const [stake, setStake] = useState("100");
  const [dropping, setDropping] = useState(false);
  const [ball, setBall] = useState(null); // {left%, top%}
  const [landed, setLanded] = useState(null); // bucket index
  const [reveal, setReveal] = useState(null);
  const [recent, setRecent] = useState([]);
  const streak = useRef(0);

  if (!isAuthed) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="plinko-signin-gate">
      <h2 className="font-display font-bold text-2xl">Sign in to play</h2>
      <Link to="/"><Button className="mt-6 font-semibold" data-testid="plinko-go-home">Go Home</Button></Link>
    </div>
  );

  const drop = async () => {
    const s = Number(stake);
    if (!s || s < 20) { toast({ title: "Minimum bet is ₹20" }); return; }
    if (s > 100000) { toast({ title: "Higher bet limit crossed", description: "Please bet ₹100000 or below." }); return; }
    if (s > balance) { toast({ title: "Insufficient balance" }); return; }
    unlockAudio();
    setDropping(true); setLanded(null);
    try {
      const res = await gamesApi.plinkoDrop({ stake: s });
      const path = res.path; // array of 0/1
      // animate: step down each row, x drifts by path
      let rights = 0;
      setBall({ left: 50, top: 0 });
      for (let r = 0; r < ROWS; r++) {
        rights += path[r];
        const pos = -0.5 * (r + 1) + rights; // peg units from center
        const leftPct = 50 + (pos / ROWS) * 80;
        await new Promise((ok) => setTimeout(ok, 160));
        setBall({ left: leftPct, top: ((r + 1) / (ROWS + 1)) * 100 });
      }
      await new Promise((ok) => setTimeout(ok, 200));
      const bucket = res.bucket;
      setBall({ left: 50 + ((bucket - ROWS / 2) / ROWS) * 90, top: 100 });
      setLanded(bucket);
      const won = res.payout > 0 && res.multiplier >= 1;
      streak.current = won ? streak.current + 1 : 0;
      setRecent((p) => [{ m: res.multiplier, won }, ...p].slice(0, 12));
      if (res.multiplier >= 1) { playCoinSplash(); setReveal({ label: `${res.multiplier}x`, won: true, payout: res.payout, streak: streak.current }); }
      await refresh();
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Try again" });
    } finally {
      setDropping(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 pb-28" data-testid="plinko-page">
      <WinReveal open={!!reveal} {...(reveal || {})} onClose={() => setReveal(null)} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl flex items-center gap-2"><CircleDot className="h-6 w-6 text-primary" /> Plinko</h1>
          <p className="text-sm text-muted-foreground">Drop the ball · land big up to 11x</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold" data-testid="plinko-balance">
          <Coins className="h-4 w-4 inline mr-1 text-primary" />{inr(balance)}
        </span>
      </div>

      {/* Board */}
      <div className="relative rounded-2xl p-4 overflow-hidden"
        style={{
          background: "radial-gradient(ellipse 120% 90% at 50% 0%, #1a2740 0%, #0b1120 60%, #05070d 100%)",
          border: "1px solid rgba(217,181,69,0.55)",
          boxShadow: "inset 0 0 0 3px rgba(217,181,69,0.22), inset 0 0 60px rgba(0,0,0,0.5), 0 10px 30px rgba(0,0,0,0.5)",
        }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 30% at 30% 8%, rgba(255,255,255,0.10), transparent 60%)" }} />
        <div className="relative h-[300px] sm:h-[340px]">
          {/* pegs */}
          {Array.from({ length: ROWS }).map((_, r) =>
            Array.from({ length: r + 3 }).map((_, c) => {
              const count = r + 3;
              const leftPct = 50 + ((c - (count - 1) / 2) / ROWS) * 80;
              const topPct = ((r + 1) / (ROWS + 1)) * 100;
              return <span key={`${r}-${c}`} className="absolute h-1.5 w-1.5 rounded-full"
                style={{ left: `${leftPct}%`, top: `${topPct}%`, transform: "translate(-50%,-50%)", background: "rgba(255,255,255,0.55)", boxShadow: "0 0 5px rgba(255,255,255,0.4)" }} />;
            })
          )}
          {/* ball */}
          {ball && (
            <span className="absolute h-3.5 w-3.5 rounded-full z-10"
              style={{
                left: `${ball.left}%`, top: `${ball.top}%`, transform: "translate(-50%,-50%)",
                background: "radial-gradient(circle at 35% 30%, #ffe9a8, #E0B84C 60%, #b45309)",
                boxShadow: "0 0 12px rgba(224,184,76,0.95)",
                transition: "left 0.16s ease-in-out, top 0.16s ease-in",
              }} data-testid="plinko-ball" />
          )}
        </div>
        {/* buckets */}
        <div className="relative grid gap-1 mt-2" style={{ gridTemplateColumns: `repeat(${MULT.length}, minmax(0,1fr))` }} data-testid="plinko-buckets">
          {MULT.map((m, i) => {
            const st = bucketStyle(m);
            const hit = landed === i;
            return (
              <div key={i} className="rounded-lg py-1.5 text-center text-[11px] font-extrabold transition-transform duration-200"
                style={{
                  background: st.background, color: st.color,
                  boxShadow: hit ? `0 0 16px rgba(${st.glow},0.95), 0 2px 6px rgba(0,0,0,0.5)` : `0 2px 6px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.25)`,
                  transform: hit ? "translateY(-3px) scale(1.12)" : "none",
                  outline: hit ? "2px solid #fde047" : "none",
                }}>
                {m}x
              </div>
            );
          })}
        </div>
      </div>

      {/* Controls */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6" data-testid="plinko-controls">
        <label className="text-sm font-medium">Stake <span className="text-xs text-muted-foreground font-normal">(min ₹20 · max ₹100,000)</span></label>
        <Input type="number" min={20} max={100000} value={stake} disabled={dropping}
          onChange={(e) => setStake(e.target.value)} className="mt-1.5 disabled:opacity-60" data-testid="plinko-stake-input" />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {[20, 50, 100, 500, 1000, 2000, 5000, 10000].map((q) => {
            const special = q === 10000;
            return (
              <button key={q} onClick={() => !dropping && setStake(String(q))} disabled={dropping} data-testid={`plinko-chip-${q}`}
                className={special
                  ? "rounded-full px-3.5 py-1.5 text-sm font-extrabold text-black shadow-md ring-1 ring-yellow-200/70 hover:brightness-110 disabled:opacity-40 transition-[filter,opacity]"
                  : "rounded-full bg-secondary px-3 py-1 text-xs font-semibold hover:bg-secondary/70 disabled:opacity-40"}
                style={special ? { background: "linear-gradient(135deg,#fde68a,#f59e0b 55%,#b45309)" } : undefined}>
                +{q}
              </button>
            );
          })}
        </div>
        <Button className="mt-5 w-full h-12 font-extrabold text-base" onClick={drop} disabled={dropping} data-testid="plinko-drop-btn">
          <CircleDot className="h-5 w-5 mr-1.5" />{dropping ? "Dropping…" : "Drop Ball"}
        </Button>
        {recent.length > 0 && (
          <div className="mt-4 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="plinko-recent">
            {recent.map((r, i) => (
              <span key={i} className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${r.won ? "bg-primary/15 text-yellow-300" : "bg-secondary text-muted-foreground"}`}>{r.m}x</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
